import { motion } from "framer-motion";
import {
  ShieldCheck,
  Sparkles,
  FileCheck2,
  UserCheck,
  TrendingUp,
  BrainCircuit,
  Percent,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Paperclip,
  DollarSign,
  Fingerprint,
  Users,
} from "lucide-react";

const FASE_0_STEPS = [
  {
    icon: FileCheck2,
    title: "Verificación de documentos",
    desc: "Revisamos que toda la documentación del cliente esté completa, vigente y sea consistente entre sí.",
  },
  {
    icon: UserCheck,
    title: "Creación del perfil migratorio",
    desc: "Armamos el perfil completo del solicitante: historial laboral, económico, familiar y de viajes según el país destino.",
  },
  {
    icon: ShieldCheck,
    title: "Verificación del perfil",
    desc: "Detectamos inconsistencias, vacíos o puntos débiles que un oficial consular podría cuestionar.",
  },
  {
    icon: TrendingUp,
    title: "Mejora del perfil",
    desc: "Reforzamos los puntos débiles: arraigo, solvencia y propósito de viaje con evidencia adicional.",
  },
  {
    icon: BrainCircuit,
    title: "Análisis con IA",
    desc: "Pasamos el perfil mejorado por nuestro sistema de IA que evalúa el caso como lo haría un cónsul.",
  },
  {
    icon: Percent,
    title: "% de aprobación real",
    desc: "Obtenemos un porcentaje estimado de aprobación antes de siquiera llenar un formulario.",
  },
];

const COUNTRIES = [
  {
    flag: "🇨🇦",
    name: "Canadá",
    accent: "red",
    intro: "Trámite 100% en línea con biometría en centro VAC.",
    steps: [
      {
        icon: FileText,
        title: "Formularios (3 por solicitante)",
        detail:
          "IMM5257 (personal) · IMM5645 (familiar) · Formulario en línea con historial obligatorio de los últimos 10 años (laboral).",
      },
      {
        icon: Paperclip,
        title: "Documentos de soporte",
        detail:
          "DNI, pasaporte, constancia laboral, fondos bancarios, acta de matrimonio, constancia de estudios y documentos adicionales según el caso.",
      },
      {
        icon: DollarSign,
        title: "Arancel consular",
        detail: "$185 USD por solicitante.",
      },
      {
        icon: Fingerprint,
        title: "Cita biométrica en VAC",
        detail: "Una vez pagado el arancel, esperamos 24 horas para solicitar la cita de datos biométricos en el Centro de Solicitud de Visa.",
      },
    ],
  },
  {
    flag: "🇺🇸",
    name: "Estados Unidos",
    accent: "blue",
    intro: "Formulario DS-160 individual + dos citas obligatorias (CAS + Entrevista).",
    steps: [
      {
        icon: FileText,
        title: "Formulario DS-160",
        detail:
          "Uno por cada solicitante, incluidos menores. Único, en línea, con historial de viajes, laboral y educativo del solicitante.",
      },
      {
        icon: Paperclip,
        title: "Documentos de soporte",
        detail:
          "Pasaporte vigente, constancia laboral, fondos bancarios, acta de matrimonio, constancia de estudios y documentos adicionales de arraigo.",
      },
      {
        icon: DollarSign,
        title: "Arancel consular",
        detail:
          "Varía según cargos vigentes del Departamento de Estado. Se confirma con el equipo antes de cada trámite (hay ajustes recientes de tarifa).",
      },
      {
        icon: Users,
        title: "Dos citas obligatorias",
        detail:
          "CAS: toma de huellas y fotografía. Entrevista consular: en la embajada o consulado correspondiente.",
      },
    ],
  },
  {
    flag: "🇲🇽",
    name: "México",
    accent: "emerald",
    intro: "Formulario impreso + entrevista consular presencial. No aplica a todas las nacionalidades.",
    steps: [
      {
        icon: FileText,
        title: "Formulario consular",
        detail:
          "Se descarga, se imprime y se firma frente al funcionario consular el día de la cita. No es 100% en línea.",
      },
      {
        icon: Paperclip,
        title: "Documentos de soporte",
        detail:
          "Pasaporte vigente, foto tamaño pasaporte y comprobante de solvencia (constancia laboral + recibos de nómina, o estados de cuenta de los últimos 3-6 meses).",
      },
      {
        icon: DollarSign,
        title: "Derechos consulares",
        detail: "Se paga en efectivo el mismo día de la cita.",
      },
      {
        icon: Users,
        title: "Entrevista consular",
        detail: "Se demuestra arraigo y solvencia económica frente al funcionario consular.",
      },
    ],
  },
];

const ACCENT_MAP = {
  red: {
    border: "border-red-400/60",
    ring: "ring-red-500/40",
    badge: "bg-red-600 text-white",
    icon: "text-red-300",
    softBg: "bg-red-500/10",
    line: "from-red-500 to-red-700",
  },
  blue: {
    border: "border-blue-400/60",
    ring: "ring-blue-500/40",
    badge: "bg-blue-700 text-white",
    icon: "text-blue-300",
    softBg: "bg-blue-500/10",
    line: "from-blue-500 to-blue-800",
  },
  emerald: {
    border: "border-emerald-400/60",
    ring: "ring-emerald-500/40",
    badge: "bg-emerald-600 text-white",
    icon: "text-emerald-300",
    softBg: "bg-emerald-500/10",
    line: "from-emerald-500 to-emerald-700",
  },
};

const WorkProcessSection = () => {
  return (
    <section
      id="trabajamos-asi"
      className="relative py-20 px-6 bg-gradient-to-br from-blue-950 via-blue-900 to-red-950 text-white overflow-hidden"
      data-testid="landing-work-process"
    >
      {/* Stars pattern */}
      <div className="absolute top-10 left-10 text-white/10 text-6xl select-none pointer-events-none">★</div>
      <div className="absolute top-32 right-20 text-white/10 text-5xl select-none pointer-events-none">★</div>
      <div className="absolute bottom-20 left-1/3 text-white/5 text-8xl select-none pointer-events-none">★</div>
      <div className="absolute top-1/2 right-1/4 text-white/5 text-7xl select-none pointer-events-none">★</div>
      <div className="absolute bottom-40 right-16 text-white/10 text-4xl select-none pointer-events-none">★</div>

      <div className="max-w-6xl mx-auto relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
          data-testid="work-process-header"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/20 border border-red-400/50 text-red-100 text-xs uppercase tracking-widest font-black mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            ★ Proceso de trabajo ★
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black mb-4 leading-tight">
            Así trabajamos <span className="text-red-300">cada asesoría</span> de visa
          </h2>
          <p className="text-blue-100 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
            Antes de llenar un solo formulario, hacemos el trabajo de fondo que{" "}
            <strong className="text-white">realmente determina</strong> si la visa se aprueba o no.
          </p>
        </motion.div>

        {/* FASE 0 — Featured card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative mb-16"
          data-testid="work-process-fase-0"
        >
          <div className="bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur border-2 border-red-400/40 rounded-3xl p-8 sm:p-10 shadow-2xl">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-red-600 text-white text-xs uppercase tracking-widest font-black mb-3">
                  ★ Fase 0 · lo más importante ★
                </span>
                <h3 className="font-heading text-3xl sm:text-4xl font-black">
                  Verificación y <span className="text-red-300">Perfil Migratorio</span>
                </h3>
              </div>
              <div className="flex items-center gap-3 bg-red-500/20 border border-red-400/40 rounded-xl px-4 py-3">
                <BrainCircuit className="w-6 h-6 text-red-200 flex-shrink-0" />
                <span className="text-sm text-red-100 font-semibold leading-tight">
                  Solo avanzamos con casos <br className="hidden sm:block" />
                  que ya sabemos que aprueban.
                </span>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {FASE_0_STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                    className="bg-white/[0.06] border border-white/10 rounded-xl p-5 hover:border-red-400/60 hover:bg-white/[0.09] transition-all"
                    data-testid={`fase-0-step-${i}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-red-500/20 border border-red-400/40 flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-red-200" />
                    </div>
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-red-300 font-black text-xs mt-0.5">0.{i + 1}</span>
                      <h4 className="font-heading text-base font-black text-white leading-tight">
                        {s.title}
                      </h4>
                    </div>
                    <p className="text-blue-100/80 text-sm leading-relaxed">{s.desc}</p>
                  </motion.div>
                );
              })}
            </div>

            {/* Decision block */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-emerald-500/10 border border-emerald-400/40 rounded-xl p-5 flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-300 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-heading font-black text-emerald-100 mb-1">
                    ✅ % Aceptable
                  </p>
                  <p className="text-emerald-100/80 text-sm leading-relaxed">
                    Avanzamos al llenado de los formularios oficiales y trámite consular.
                  </p>
                </div>
              </div>
              <div className="bg-amber-500/10 border border-amber-400/40 rounded-xl p-5 flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-300 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-heading font-black text-amber-100 mb-1">
                    ⚠️ % No aceptable
                  </p>
                  <p className="text-amber-100/80 text-sm leading-relaxed">
                    Seguimos trabajando el perfil. <strong>Nunca</strong> sometemos un caso débil a entrevista consular.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-center text-blue-100/70 text-sm sm:text-base italic mt-8 max-w-3xl mx-auto">
              Este es el <strong className="text-white not-italic">verdadero valor de Divídelo</strong>: no llenamos formularios a ciegas — solo avanzamos con casos que ya sabemos que tienen una probabilidad real de éxito.
            </p>
          </div>
        </motion.div>

        {/* Header: process by country */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="inline-block px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-xs uppercase tracking-widest font-black mb-4">
            ★ Fase 1 · Proceso por país ★
          </span>
          <h3 className="font-heading text-3xl sm:text-4xl font-black mb-3">
            Cada país, su <span className="text-red-300">propio camino</span>
          </h3>
          <p className="text-blue-100/80 max-w-2xl mx-auto">
            Después de aprobar la Fase 0, iniciamos el trámite formal según el destino.
          </p>
        </motion.div>

        {/* Countries grid */}
        <div className="grid lg:grid-cols-3 gap-6" data-testid="work-process-countries">
          {COUNTRIES.map((country, i) => {
            const style = ACCENT_MAP[country.accent];
            return (
              <motion.div
                key={country.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`bg-white/[0.06] backdrop-blur border-2 ${style.border} rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all`}
                data-testid={`country-card-${country.name.toLowerCase()}`}
              >
                {/* Country header */}
                <div className={`${style.softBg} px-6 pt-6 pb-5 border-b border-white/10`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-5xl leading-none">{country.flag}</span>
                    <div>
                      <span className={`inline-block ${style.badge} text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full mb-1`}>
                        Fase 1
                      </span>
                      <h4 className="font-heading text-2xl font-black text-white leading-none">
                        {country.name}
                      </h4>
                    </div>
                  </div>
                  <p className="text-blue-100/85 text-sm leading-relaxed">{country.intro}</p>
                </div>

                {/* Steps */}
                <div className="p-6 space-y-4">
                  {country.steps.map((step, idx) => {
                    const Icon = step.icon;
                    return (
                      <div
                        key={idx}
                        className="flex items-start gap-3"
                        data-testid={`country-step-${country.name.toLowerCase()}-${idx}`}
                      >
                        <div className={`flex-shrink-0 w-9 h-9 rounded-lg ${style.softBg} border border-white/10 flex items-center justify-center`}>
                          <Icon className={`w-4 h-4 ${style.icon}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-heading font-black text-white text-sm mb-0.5">
                            {step.title}
                          </p>
                          <p className="text-blue-100/70 text-xs leading-relaxed">
                            {step.detail}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Mexico note */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-6 max-w-3xl mx-auto bg-white/5 border border-white/10 rounded-xl p-4 text-center text-sm text-blue-100/80"
        >
          <strong className="text-white">Nota:</strong> no todas las nacionalidades requieren visa para ingresar a México como turistas — esto aplica solo a los casos donde el cliente sí la necesita.
        </motion.div>

        {/* Closing statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-14 max-w-4xl mx-auto text-center"
        >
          <div className="bg-gradient-to-r from-red-600/20 via-white/5 to-blue-600/20 border border-white/20 rounded-2xl p-6 sm:p-8">
            <p className="text-red-300 text-xs uppercase tracking-widest font-black mb-3">
              ★ El estándar Divídelo ★
            </p>
            <p className="text-white text-lg sm:text-xl leading-relaxed">
              En los <strong className="text-red-300">3 países</strong> el enfoque es el mismo: verificamos y{" "}
              <strong className="text-white">fortalecemos el perfil</strong> antes de avanzar, documentación completa, formularios sin errores y{" "}
              <strong className="text-white">evidencia sólida de arraigo</strong> — para maximizar la probabilidad real de aprobación en cada caso.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WorkProcessSection;
